'use client';

import { useCart } from '@veranda/shared/cart/provider';
import { useEffect } from 'react';

const GWP_REMOVAL_PREFIX = 'gwp-removed-';

/**
 * useGWPRemovalTracking provides helpers to track the manual removal of
 * Gift With Purchase (GWP) items from the cart using sessionStorage.
 * It allows marking a GWP as removed, checking if a GWP was removed,
 * clearing a specific GWP removal, and clearing all GWP removals.
 * When the cart is emptied, all GWP removal markers are cleared.
 */
export function useGWPRemovalTracking() {
  const { cart } = useCart();

  useEffect(() => {
    if (!cart?.items || cart.items.length === 0) {
      clearAllGWPRemovals();
    }
  }, [cart?.items]);

  const markGWPAsRemoved = (variantId: string) => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(`${GWP_REMOVAL_PREFIX}${variantId}`, 'true');
    }
  };

  const isGWPManuallyRemoved = (variantId: string): boolean => {
    if (typeof window === 'undefined') return false;
    return (
      sessionStorage.getItem(`${GWP_REMOVAL_PREFIX}${variantId}`) === 'true'
    );
  };

  const clearGWPRemoval = (variantId: string) => {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(`${GWP_REMOVAL_PREFIX}${variantId}`);
    }
  };

  const clearAllGWPRemovals = () => {
    if (typeof window === 'undefined') return;

    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(GWP_REMOVAL_PREFIX)) {
        keysToRemove.push(key);
      }
    }

    for (const key of keysToRemove) {
      sessionStorage.removeItem(key);
    }
  };

  return {
    markGWPAsRemoved,
    isGWPManuallyRemoved,
    clearGWPRemoval,
    clearAllGWPRemovals,
  };
}
